import type { Enterprise } from '@pn/core/domain/enterprise';
import type { UserSettings } from '@pn/core/domain/settings';
import type {
  InvitedUser,
  User,
  UserForm,
  UserPlans,
} from '@pn/core/domain/user';
import { setAvailableStackLayers } from '@pn/core/storage/workspace/mappings';
import { assert } from '@pn/core/utils/assert';
import { pnApiClient } from '@pn/services/api/pnApiClient';
import { webStorageService } from '@pn/services/storage/webStorageService';
import { isNil } from 'lodash-es';
import { apiInvitedUserMapper, apiUserMapper } from './apiUserMapper';
import { deduceUserSettings } from './apiUserSettingsMapper';
import {
  isApiEnterpriseInvitedUser,
  isApiUser,
  type ApiCheckoutSession,
  type ApiEnterpriseUser,
  type ApiInviteDetails,
  type ApiUser,
} from './types';

const getUserEvent = import.meta.env.VITE_APP_MIXPANEL_GET_USER_EVENT;

const getCurrentUser = async () => {
  const invite = webStorageService('pn').get<string>('invite-string');

  let getUserUrl = 'v2/user';
  if (!isNil(getUserEvent) && getUserEvent !== '') {
    getUserUrl = `${getUserUrl}?mixpanel_event=${encodeURIComponent(getUserEvent)}`;
  }

  const apiUser = await pnApiClient.request<ApiUser>({
    url: invite ? 'v2/user?invite=' + encodeURIComponent(invite) : getUserUrl,
  });

  const user = apiUserMapper.toDomainUser(apiUser);

  const stackLayers = user.enterprise?.stackLayers ?? {};
  setAvailableStackLayers(stackLayers);

  return {
    user,
    settings: deduceUserSettings(apiUser.user_config),
  };
};

const getUsersByEnterpriseIdAsAdmin = async (
  enterpriseId: Enterprise['id']
) => {
  const { users: apiUsers } = await pnApiClient.request<{
    users: ApiEnterpriseUser[];
  }>({
    url: `v2/admin/users?page=1&page_size=1000&enterprise_id=${enterpriseId}`, // TODO: hardcoded because we're re-using a paginaged endpoint
  });

  const users = apiUsers.map((user) => {
    if (isApiUser(user)) {
      return apiUserMapper.toDomainUser(user);
    } else {
      throw new Error('Invalid api user');
    }
  });

  return users;
};

const getInvitedUsersByEnterpriseId = async (
  enterpriseId: Enterprise['id']
) => {
  const apiUsers = await pnApiClient.request<ApiUser[]>({
    url: `v1/invites?enterprise_id=${enterpriseId}`,
  });

  const users = apiUsers.map((user) => {
    if (isApiEnterpriseInvitedUser(user)) {
      return apiInvitedUserMapper.toDomainInvitedUser(user);
    } else {
      throw new Error('Invalid api invited user');
    }
  });

  return users;
};

const getPaginatedUsersByParams = async (param: string) => {
  const { users: apiUsers, total_rows: rowCount } = await pnApiClient.request<{
    users: ApiUser[];
    total_rows: number;
  }>({
    url: `v2/admin/users${param}`,
  });

  const users = apiUsers.map((user) => {
    if (isApiUser(user)) {
      return apiUserMapper.toDomainUser(user);
    } else {
      throw new Error('Invalid api user');
    }
  });

  return { users, rowCount };
};

/**
 * Exports every user matching the filters and sort as CSV text (pagination
 * params are ignored by the API). Cells starting with a formula character are
 * already prefixed with `'` by the server, so don't strip it.
 */
const exportUsersAsCsvAsAdmin = async (param: string) => {
  return pnApiClient.request<string>({
    url: `v2/admin/users/export${param}`,
  });
};

const getUsersBySearchQuery = async (searchQuery: string) => {
  const apiUsers = await pnApiClient.request<ApiUser[]>({
    url: `v2/admin/users/search?search_term=${searchQuery}`,
  });

  const users = apiUsers.map((user) => {
    if (isApiUser(user)) {
      return apiUserMapper.toDomainUser(user);
    } else {
      throw new Error('Invalid api user');
    }
  });

  return users;
};

const updateCurrentUserProfile = async (userForm: UserForm) => {
  return pnApiClient.request<void>({
    method: 'PUT',
    url: 'v2/user',
    payload: apiUserMapper.toTargetUserProfile(userForm),
  });
};

const updateUserConfig = async (settings: UserSettings) => {
  return pnApiClient.request<void>({
    method: 'POST',
    url: 'v2/user/config',
    payload: {
      user_config: settings,
    },
  });
};

const deleteCurrentUser = async () => {
  return pnApiClient.request<void>({
    url: 'v2/user',
    method: 'DELETE',
  });
};

const addUserToEnterpriseAsAdmin = async (params: {
  user: User;
  enterprise: Enterprise;
  userPlan: UserPlans.EnterpriseAdmin | UserPlans.EnterpriseMember;
}) => {
  const { user, enterprise, userPlan } = params;

  return pnApiClient.request<void>({
    method: 'PUT',
    url: `v2/admin/users/${user.id}`,
    payload: {
      ...apiUserMapper.toTargetUser({ ...user, enterprise, userPlan }),
      enterprise_id: enterprise.id, // TODO: the API should anticipate a nested user.enterprise structure instead
    },
  });
};

const createUserAsAdmin = async (
  user: Pick<User, 'email' | 'name'>,
  password: string
) => {
  return pnApiClient.request<{ id?: User['id'] }>({
    method: 'POST',
    url: `v2/admin/users`,
    payload: {
      email: user.email,
      first_name: user.name.firstName,
      last_name: user.name.lastName,
      password,
    },
  });
};

const updateUserAsAdmin = async (user: User) => {
  return pnApiClient.request<void>({
    method: 'PUT',
    url: `v2/admin/users/${user.id}`,
    payload: apiUserMapper.toTargetUser(user),
  });
};

const updateEnterpriseUser = async (user: User) => {
  assert(user.enterprise, 'User must have an enterprise property');

  return pnApiClient.request<void>({
    method: 'PUT',
    url: `v1/enterprises/${user.enterprise.id}/users/${user.id}`,
    payload: apiUserMapper.toTargetUser(user),
  });
};

const removeEnterpriseUser = async (user: User) => {
  assert(user.enterprise, 'User must have an enterprise property');

  return pnApiClient.request<void>({
    method: 'DELETE',
    url: `v1/enterprises/${user.enterprise.id}/users/${user.id}`,
  });
};

const inviteUser = async (invitedUser: InvitedUser) => {
  return pnApiClient.request<{ id: InvitedUser['id'] }>({
    method: 'POST',
    url: 'v1/invites',
    payload: {
      email: invitedUser.email,
      user_plan: invitedUser.userPlan,
      enterprise_id: invitedUser.enterprise?.id,
    },
  });
};

const deleteInvite = async (invitedUserId: InvitedUser['id']) => {
  return pnApiClient.request<void>({
    method: 'DELETE',
    url: `v1/invites/${invitedUserId}`,
  });
};

const getInviteDetails = async (invite: string) => {
  return pnApiClient.request<ApiInviteDetails>({
    url: 'v1/invite',
    payload: { invite },
  });
};

const acceptInvite = async (invite: string) => {
  return pnApiClient.request<void>({
    method: 'POST',
    url: 'v1/invites/accept?invite=' + encodeURIComponent(invite),
  });
};

const declineInvite = async (invite: string) => {
  return pnApiClient.request<void>({
    method: 'POST',
    url: 'v1/invites/decline?invite=' + encodeURIComponent(invite),
  });
};

const createCheckoutSession = async (params: {
  priceId: string;
  successUrl: string;
  cancelUrl: string;
}) => {
  const { priceId, successUrl, cancelUrl } = params;

  return pnApiClient.request<ApiCheckoutSession>({
    url: 'v2/checkout',
    method: 'POST',
    payload: {
      price_id: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
    },
  });
};

export const apiUserProvider = {
  getCurrentUser,
  getUsersByEnterpriseIdAsAdmin,
  getInvitedUsersByEnterpriseId,
  getUsersBySearchQuery,
  getPaginatedUsersByParams,
  exportUsersAsCsvAsAdmin,
  updateCurrentUserProfile,
  updateUserConfig,
  deleteCurrentUser,
  addUserToEnterpriseAsAdmin,
  createUserAsAdmin,
  updateUserAsAdmin,
  updateEnterpriseUser,
  removeEnterpriseUser,
  inviteUser,
  deleteInvite,
  getInviteDetails,
  acceptInvite,
  declineInvite,
  createCheckoutSession,
};
